import React, {
  type ChangeEvent,
  type CSSProperties,
  useRef,
} from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import {
  CenterFlex,
  Divider,
  Flex,
  ScrollVFlex,
  Space,
  VFlex,
} from '@/components/base';
import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { useSidebarWidth } from '@/hooks/domain/useSidebar';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import type {
  DETAIL_MODAL_TYPE,
  DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { detailModalStyle } from '@/styles/style';
import { FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR } from '@/types/feedbackConst';

import FeedbackDetailBodyArea from '../FeedbackBody/FeedbackDetailBodyArea';
import FeedbackDetailPropertyArea from '../PropertyArea/FeedbackDetailPropertyArea';
import useFeedbackDetail from '../useFeedbackDetail';
import FeedbackDetailModalActions from './FeedbackDetailHeader/FeedbackDetailModalActions';
import FeedbackDetailActions from './FeedbackDetailHeader/FeedbackModalActions';

export type FeedbackDetailModalProps = {
  tableKey?: string;
  feedbackAlias: string;
  pos?: number;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
  style?: CSSProperties;
  options?: DetailModalOptions<{
    // exists only when feedback is chat type
    sourceId?: string;
    focusedBlock?: string;
    hideTopicTab?: boolean;
  }>;
};

export const AI_SUMMARY_NEW_LINE_SEPERATOR = '\n';

const FeedbackDetailModal = (props: FeedbackDetailModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { isEditor } = useUserRole();
  const [showFull, setShowFull] = useWorkspaceStorage<boolean>(
    STORAGE_KEYS.FEEDBACK_DETAIL_COLLAPSE_PROPERTY_KEY,
    false,
  );
  const detailProps = useFeedbackDetail(props);
  const {
    setValue,
    detail,
    feedbackDetailFullView,
    onBlurHandler,
    empty,
    loading,
  } = detailProps;

  const onTitleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, 200);
    setValue('title', val);
  };

  const parsedAISummary: string[] = detail?.summary
    ? detail.summary.split(AI_SUMMARY_NEW_LINE_SEPERATOR)
    : [];

  const sidebarWidth = useSidebarWidth();

  useTrackModal({
    domain: 'feedbacks',
    alias: props.feedbackAlias,
  });

  useKeymap('Escape', props.closeModal);

  return (
    <ModalStyle
      pos={props.pos || 0}
      sidebarWidth={sidebarWidth}
      feedbackDetailFullView={feedbackDetailFullView}
      style={props.style}
      ref={modalRef}
    >
      <Flex a="center" j="space-between" style={{ padding: '11px 16px' }}>
        <Flex a="center" g={8}>
          <FeedbackDetailModalActions
            closeModal={detailProps.closeModal}
            detail={(detail as FeedbackT) || undefined}
          />
          <Spinner status={loading} />
        </Flex>

        <FeedbackDetailActions
          feedbackAlias={props.feedbackAlias}
          closeModal={detailProps.closeModal}
          onSelectNewModal={props.onSelectNewModal}
          toPrevFeedback={detailProps.toPrevFeedback}
          toNextFeedback={detailProps.toNextFeedback}
        />
      </Flex>

      <Transition
        id={FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR(props.feedbackAlias)}
        style={{ flex: 1, overflow: 'auto' }}
        status={!!detail || !loading}
        fallback={
          <CenterFlex style={{ height: '100%' }}>
            <Spinner />
          </CenterFlex>
        }
      >
        {empty && (
          <CenterFlex d="column" g={24} height="100%">
            <MainText dim>Data does not exist anymore</MainText>
            <Button onClick={props.closeModal}>Close</Button>
          </CenterFlex>
        )}
        {!!detail &&
          (!feedbackDetailFullView ? (
            <VFlex f={1}>
              <Wrapper>
                <Flex g={12}>
                  <FlexibleTitleInput
                    value={detail.title}
                    placeholder="Untitled"
                    onChange={onTitleChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    readOnly={!isEditor}
                    onBlur={onBlurHandler}
                    style={{
                      marginLeft: -12,
                    }}
                    showBg={false}
                  />
                </Flex>
                <Space h="10px" />
                <FeedbackDetailPropertyArea
                  key={detail.alias}
                  detailProps={{
                    ...detailProps,
                    detail,
                  }}
                  showFull={showFull}
                  setShowFull={setShowFull}
                />
                {detail?.summary && (
                  <>
                    <Space h="24px" />
                    <SummaryBox parsedAISummary={parsedAISummary} />
                  </>
                )}
                <Space h="24px" />
                <FeedbackDetailBodyArea {...detailProps} />
                <Space h="60px" />
              </Wrapper>
            </VFlex>
          ) : (
            <Flex className="full-view" f={1} j="center">
              <ScrollVFlex className="feedback-body-area">
                <div className="feedback-body-area-inner">
                  <Flex g={12}>
                    <FlexibleTitleInput
                      placeholder="Untitled"
                      style={{ marginLeft: -12 }}
                      value={detail.title}
                      onChange={onTitleChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      readOnly={!isEditor}
                      onBlur={onBlurHandler}
                      showBg={false}
                    />
                  </Flex>
                  {detail?.summary && (
                    <>
                      <Space h="12px" />
                      <SummaryBox parsedAISummary={parsedAISummary} />
                    </>
                  )}
                  <Space h="12px" />
                  <FeedbackDetailBodyArea {...detailProps} />
                </div>
              </ScrollVFlex>
              <Divider direction="v" />
              <ScrollVFlex className="feedback-property-area">
                <FeedbackDetailPropertyArea
                  key={detail.alias}
                  detailProps={{
                    ...detailProps,
                    detail,
                  }}
                  showFull={showFull}
                  setShowFull={setShowFull}
                />

                <Space h="24px" />
              </ScrollVFlex>
            </Flex>
          ))}
      </Transition>
    </ModalStyle>
  );
};

const SummaryBox = (props: { parsedAISummary: string[] }) => {
  return (
    <React.Fragment>
      <AISummaryBox>
        <Flex a="center" g="2px">
          <AIIcon />
          <Typo type="label3" className="text-gray-700" weight="sb">
            AI summary
          </Typo>
        </Flex>
        <ul>
          {props.parsedAISummary.map((summary, index) => (
            <li key={index}>
              <Typo
                type="body2"
                className="text-gray-800"
                style={{ fontSize: 13, lineHeight: 1.6 }}
              >
                {summary}
              </Typo>
            </li>
          ))}
        </ul>
      </AISummaryBox>
    </React.Fragment>
  );
};

type ModalStyleProps = {
  pos: number;
  feedbackDetailFullView: boolean;
  sidebarWidth: number;
};

const ModalStyle = styled(VFlex)<ModalStyleProps>`
  position: fixed;
  width: ${({ feedbackDetailFullView, sidebarWidth }) =>
    feedbackDetailFullView ? `calc(100vw - ${sidebarWidth}px)` : '600px'};
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
  .full-view {
    height: 100%;
    .feedback-body-area {
      padding: 12px 24px 48px;
      flex: 1;
    }
    .feedback-body-area-inner {
      width: 100%;
      max-width: 800px;
      margin: 0 auto;
    }
    .feedback-property-area {
      padding: 12px 24px 48px;
      max-width: 520px;
      width: 100%;
    }
  }
`;

const Wrapper = styled.div`
  position: relative;
  padding: 14px 28px 24px;
  .modal-header {
    width: 100%;
  }
`;

const AISummaryBox = styled(VFlex)`
  gap: 6px;
  background-color: ${(props) => props.theme.colors.gray200};
  padding: 12px;
  border-radius: 6px;
  ul {
    margin: 0;
  }
`;

export default FeedbackDetailModal;
